import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  RefreshCw,
  Package,
  Warehouse,
  Calendar,
  AlertTriangle,
  Check,
  ArrowRight,
  ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { motion } from "framer-motion";

const RefillPage = () => {
  const [compartments, setCompartments] = useState([]);
  const [stock, setStock] = useState([]);
  const [refills, setRefills] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCompartment, setSelectedCompartment] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [selectedStockId, setSelectedStockId] = useState("");
  const [availableStock, setAvailableStock] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [compRes, stockRes, refillsRes] = await Promise.all([
        axios.get(`${API}/compartments`),
        axios.get(`${API}/stock`),
        axios.get(`${API}/refills?limit=20`)
      ]);
      setCompartments(compRes.data);
      setStock(stockRes.data);
      setRefills(refillsRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const openRefillDialog = async (compartment) => {
    if (!compartment.product_id) {
      toast.error("Fach hat kein Produkt zugewiesen");
      return;
    }
    
    setSelectedCompartment(compartment);
    setQuantity("");
    setSelectedStockId("");
    
    // Get available stock for this product
    try {
      const res = await axios.get(`${API}/stock/${compartment.product_id}`);
      setAvailableStock(res.data);
    } catch (err) {
      setAvailableStock([]);
    }
    
    setDialogOpen(true);
  };

  const handleRefill = async () => {
    if (!quantity || parseInt(quantity) <= 0) {
      toast.error("Bitte gültige Menge eingeben");
      return;
    }

    try {
      await axios.post(`${API}/refills`, {
        compartment_id: selectedCompartment.id,
        quantity: parseInt(quantity),
        stock_id: selectedStockId || null
      });

      toast.success("Auffüllung erfolgreich");
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Auffüllen");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "empty": return "border-red-300 bg-red-50";
      case "low": return "border-amber-300 bg-amber-50";
      case "full": return "border-emerald-300 bg-emerald-50";
      default: return "border-slate-200";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("de-DE");
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Group compartments by row (A, B, C, etc.)
  const groupedCompartments = compartments.reduce((acc, comp) => {
    const row = comp.slot_id?.[0] || "?";
    if (!acc[row]) acc[row] = [];
    acc[row].push(comp);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="refill-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
          Auffüllen
        </h1>
        <p className="text-slate-500 mt-1">
          Produkte aus dem Lager in den Automaten füllen
        </p>
      </div>

      {/* Process Info */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-4">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Warehouse className="w-5 h-5 text-blue-600" />
              <span className="font-medium">Lager</span>
            </div>
            <ArrowRight className="w-4 h-4 text-blue-400" />
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              <span className="font-medium">Automat-Fach</span>
            </div>
            <span className="text-blue-600 ml-auto">
              FIFO: Älteste Charge wird automatisch verwendet
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Compartments Grid */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Automaten-Fächer
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(groupedCompartments).length === 0 ? (
            <p className="text-center text-slate-500 py-8">
              Keine Fächer konfiguriert
            </p>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedCompartments).sort().map(([row, comps]) => (
                <div key={row}>
                  <p className="text-sm font-medium text-slate-500 mb-2">Reihe {row}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    {comps.sort((a, b) => a.slot_id?.localeCompare(b.slot_id)).map((comp) => (
                      <motion.div
                        key={comp.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`relative p-4 rounded-xl border-2 cursor-pointer transition-colors ${getStatusColor(comp.status)}`}
                        onClick={() => openRefillDialog(comp)}
                      >
                        {/* Slot ID Badge */}
                        <Badge className="absolute -top-2 -right-2 bg-slate-700">
                          {comp.slot_id}
                        </Badge>

                        {/* Content */}
                        <div className="text-center">
                          {comp.product_name ? (
                            <>
                              <p className="font-medium text-sm truncate">
                                {comp.product_name}
                              </p>
                              <p className="text-2xl font-bold mt-1">
                                {comp.current_stock}
                                <span className="text-sm font-normal text-slate-500">
                                  /{comp.max_capacity}
                                </span>
                              </p>
                              {comp.expiry_date && (
                                <div className="flex items-center justify-center gap-1 mt-1 text-xs text-slate-500">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(comp.expiry_date)}
                                </div>
                              )}
                            </>
                          ) : (
                            <p className="text-slate-400 text-sm py-4">Leer</p>
                          )}
                        </div>

                        {/* Fill indicator */}
                        {comp.product_name && (
                          <div className="mt-2 h-1 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                comp.status === "empty" ? "bg-red-500" :
                                comp.status === "low" ? "bg-amber-500" :
                                "bg-emerald-500"
                              }`}
                              style={{ width: `${(comp.current_stock / comp.max_capacity) * 100}%` }}
                            />
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Refills */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Letzte Auffüllungen
          </CardTitle>
        </CardHeader>
        <CardContent>
          {refills.length === 0 ? (
            <p className="text-center text-slate-500 py-4">
              Noch keine Auffüllungen
            </p>
          ) : (
            <div className="space-y-2">
              {refills.map((refill) => (
                <div
                  key={refill.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Check className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium">{refill.product_name}</p>
                      <p className="text-sm text-slate-500">
                        Fach {refill.slot_id} • +{refill.quantity} Stück
                      </p>
                    </div>
                  </div>
                  <div className="text-right text-sm text-slate-500">
                    {formatDateTime(refill.refilled_at)}
                    {refill.expiry_date && (
                      <p className="text-xs">MHD: {formatDate(refill.expiry_date)}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Refill Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Fach auffüllen
            </DialogTitle>
          </DialogHeader>

          {selectedCompartment && (
            <div className="space-y-4 py-4">
              {/* Compartment Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{selectedCompartment.product_name}</p>
                    <p className="text-sm text-slate-500">Fach {selectedCompartment.slot_id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      {selectedCompartment.current_stock}
                      <span className="text-sm font-normal text-slate-500">
                        /{selectedCompartment.max_capacity}
                      </span>
                    </p>
                    <p className="text-xs text-slate-500">aktuell</p>
                  </div>
                </div>
              </div>

              {/* Stock Selection */}
              {availableStock.length > 0 ? (
                <div className="space-y-2">
                  <Label>Lagercharge (FIFO empfohlen)</Label>
                  <Select value={selectedStockId} onValueChange={setSelectedStockId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Auto (älteste zuerst)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Auto (älteste Charge)</SelectItem>
                      {availableStock.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.quantity} Stk. - MHD: {formatDate(s.expiry_date)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Available stock info */}
                  <div className="text-sm text-slate-500">
                    Verfügbar im Lager: {availableStock.reduce((sum, s) => sum + s.quantity, 0)} Stück
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">Kein Lagerbestand vorhanden</span>
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    Bitte erst Wareneingang buchen
                  </p>
                </div>
              )}

              {/* Quantity Input */}
              <div className="space-y-2">
                <Label>Menge auffüllen</Label>
                <Input
                  type="number"
                  min="1"
                  max={selectedCompartment.max_capacity - selectedCompartment.current_stock}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder={`max. ${selectedCompartment.max_capacity - selectedCompartment.current_stock}`}
                />
                <p className="text-xs text-slate-500">
                  Freie Kapazität: {selectedCompartment.max_capacity - selectedCompartment.current_stock} Stück
                </p>
              </div>

              {/* Preview */}
              {quantity && parseInt(quantity) > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm text-emerald-700">
                    Nach Auffüllung: <strong>
                      {selectedCompartment.current_stock + parseInt(quantity)}
                    </strong> von {selectedCompartment.max_capacity} Stück
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button
                  onClick={handleRefill}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={!quantity || parseInt(quantity) <= 0 || availableStock.length === 0}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Auffüllen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RefillPage;
